import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaRegCopy, FaEdit, FaTrash } from "react-icons/fa";
import "./Survey.css";
import PaymentModal from "./PaymentModal"; // Adjust path if needed

/****************************************************
 * Helper: Calculate days since link_created_at
 ****************************************************/
function getDaysOpen(survey) {
  if (!survey.link_created_at) return null;
  const linkDate = new Date(survey.link_created_at);
  const now = new Date();
  const diffMs = now - linkDate;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

function Survey() {
  const navigate = useNavigate();

  // Survey creation/editing
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [editSurveyId, setEditSurveyId] = useState(null);

  // Surveys list
  const [surveys, setSurveys] = useState([]);
  const createRef = useRef(null);

  // Payment Modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [rawInput, setRawInput] = useState("");
  const [recipientCount, setRecipientCount] = useState("");

  // Analysis overlay
  const [analysisInProgress, setAnalysisInProgress] = useState(false);

  // State for our "show full text" modal
  const [showFullModal, setShowFullModal] = useState(false);
  const [fullModalTitle, setFullModalTitle] = useState("");
  const [fullModalText, setFullModalText] = useState("");

  // Track minimized survey cards
  const [minimizedCards, setMinimizedCards] = useState(new Set());

  useEffect(() => {
    loadSurveys();
  }, []);

  // === ADDED to disable/enable the nav bar via body class
  useEffect(() => {
    if (analysisInProgress) {
      document.body.classList.add("nav-disabled");
    } else {
      document.body.classList.remove("nav-disabled");
    }
    // Cleanup if the component unmounts
    return () => {
      document.body.classList.remove("nav-disabled");
    };
  }, [analysisInProgress]);

  /****************************************************
   * Load Surveys
   ****************************************************/
  const loadSurveys = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.warn("[Survey] No auth token, skipping loadSurveys");
        return;
      }
      const response = await fetch("/surveys", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        console.error("[Survey] loadSurveys failed, status:", response.status);
        return;
      }
      const data = await response.json();
      setSurveys(data.surveys || []);
    } catch (error) {
      console.error("[Survey] loadSurveys error:", error);
    }
  };

  /****************************************************
   * Create a new survey
   ****************************************************/
  const handleCreateSurvey = async () => {
    if (!title || !prompt) {
      alert("Please enter both a Title and a Prompt.");
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        alert("No auth token found. Please log in first.");
        return;
      }
      const response = await fetch("/surveys", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title, prompt }),
      });
      if (!response.ok) {
        const err = await response.json();
        alert(`Error saving survey: ${err.message || "Unknown error"}`);
        return;
      }
      alert("Survey created!");
      setTitle("");
      setPrompt("");
      setEditSurveyId(null);
      loadSurveys();
    } catch (error) {
      console.error("[Survey] handleCreateSurvey error:", error);
      alert("An error occurred while saving the survey.");
    }
  };

  /****************************************************
   * Editing Survey
   ****************************************************/
  const handleEditSurvey = (survey) => {
    setEditSurveyId(survey.id);
    setTitle(survey.title || "");
    setPrompt(survey.prompt || "");
    createRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Save changes
  const handleUpdateSurvey = async () => {
    if (!title || !prompt) {
      alert("Title and prompt cannot be empty.");
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`/surveys/${editSurveyId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ title, prompt }),
      });
      if (!response.ok) {
        const err = await response.json();
        alert(err.error || "Error updating survey");
        return;
      }
      alert("Survey updated successfully!");
      setTitle("");
      setPrompt("");
      setEditSurveyId(null);
      loadSurveys();
    } catch (error) {
      console.error("[Survey] handleUpdateSurvey error:", error);
    }
  };

  /****************************************************
   * Delete Survey
   ****************************************************/
  const handleDeleteSurvey = async (surveyId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this survey?"
    );
    if (!confirmDelete) return;
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`/surveys/${surveyId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json();
        alert(err.error || "Error deleting survey");
        return;
      }
      alert("Survey deleted.");
      loadSurveys();
    } catch (error) {
      console.error("[Survey] handleDeleteSurvey error:", error);
    }
  };

  /****************************************************
   * Close Survey
   ****************************************************/
  const handleCloseSurvey = async (surveyId) => {
    const confirmClose = window.confirm(
      "Are you sure you want to close this survey? No more responses will be accepted."
    );
    if (!confirmClose) return;
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`/surveys/${surveyId}/close`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json();
        alert(err.error || "Error closing survey");
        return;
      }
      alert("Survey closed. No more responses allowed.");
      loadSurveys();
    } catch (error) {
      console.error("[Survey] handleCloseSurvey error:", error);
    }
  };

  /****************************************************
   * AI Analysis
   ****************************************************/
  const handleAnalysis = async (survey) => {
    // If analysis was already requested, navigate directly
    if (survey.analysis_requested) {
      navigate(`/analysis/${survey.id}`);
      return;
    }
    const confirmRequest = window.confirm(
      "Are you sure you want to request AI analysis?"
    );
    if (!confirmRequest) return;

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        alert("You must be logged in to request analysis.");
        return;
      }

      // Force scroll to top before showing overlay
      window.scrollTo(0, 0);

      setAnalysisInProgress(true);
      const response = await fetch(`/analysis/${survey.id}/generate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        setAnalysisInProgress(false);
        let errMsg = "Error requesting analysis";
        try {
          const errData = await response.json();
          if (errData.error) {
            errMsg = errData.error;
          }
        } catch {}
        alert(errMsg);
        return;
      }
      pollAnalysisCompletion(survey.id);
    } catch (error) {
      setAnalysisInProgress(false);
      alert("An error occurred while requesting analysis. Please try again.");
    }
  };

  /**
   * Poll the server until the analysis is complete (indefinite approach).
   */
  const pollAnalysisCompletion = (surveyId) => {
    const intervalId = setInterval(async () => {
      try {
        const token = localStorage.getItem("authToken");
        const resp = await fetch(`/analysis/${surveyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const data = await resp.json();
          // If bulletPoints exist, analysis is presumably done
          if (data.bulletPoints && data.bulletPoints.length > 0) {
            clearInterval(intervalId);
            setAnalysisInProgress(false);
            await loadSurveys();
            navigate(`/analysis/${surveyId}`);
          }
        }
      } catch (err) {
        console.error("[Survey] Poll error:", err);
      }
      // no limit to attempts
    }, 3000);
  };

  /****************************************************
   * Copy Link
   ****************************************************/
  const copyToClipboard = (link) => {
    navigator.clipboard
      .writeText(link)
      .then(() => {
        alert("Link copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy link:", err);
      });
  };

  /****************************************************
   * Payment / Link Generation
   ****************************************************/
  const handleOpenPaymentModal = (survey) => {
    setSelectedSurvey(survey);
    setShowPaymentModal(true);
  };

  // If user sets some recipients count
  const validateRecipientInput = () => {
    const val = parseInt(rawInput, 10);
    if (!isNaN(val)) {
      const validated = Math.min(Math.max(val, 10), 1000);
      setRecipientCount(validated);
      setRawInput(validated.toString());
    } else {
      setRecipientCount("");
      setRawInput("");
    }
  };

  // For free survey => generate link directly
  const handleFreeGenerateLink = async (surveyId) => {
    try {
      const token = localStorage.getItem("authToken");
      const resp = await fetch(`/surveys/${surveyId}/generate-link`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const data = await resp.json();
        alert(data.error || "Something went wrong generating the free link.");
        return;
      }
      const { message, link } = await resp.json();
      alert(`Free link generated successfully!\n${link}`);
      await loadSurveys();
    } catch (err) {
      console.error("Error generating free link:", err);
      alert("Network error generating free link.");
    }
  };

  /****************************************************
   * Full text modal / Minimizing
   ****************************************************/
  const handleShowFullText = (fieldName, text) => {
    setFullModalTitle(fieldName);
    setFullModalText(text || "");
    setShowFullModal(true);
  };

  const toggleMinimize = (surveyId) => {
    setMinimizedCards((prevSet) => {
      const newSet = new Set(prevSet);
      if (newSet.has(surveyId)) {
        newSet.delete(surveyId);
      } else {
        newSet.add(surveyId);
      }
      return newSet;
    });
  };

  const isEditing = !!editSurveyId;

  /****************************************************
   * RENDER
   ****************************************************/
  return (
    <div className="survey-container" style={{ position: "relative" }}>
      <h1>My Feedback</h1>
      <p style={{ textAlign: "center" }}>
        <strong>
          This is where you can create, edit, and save your Feedback Groups.
        </strong>
      </p>

      {/* CREATE or EDIT SURVEY */}
      <div ref={createRef} className="create-survey-section">
        <h2>{isEditing ? "Edit Survey" : "Create a New Feedback Group"}</h2>

        {!isEditing && (
          <p>
            • Enter your title and the main topic or question you want to
            ask.
            <br />
            • You can give some context or instructions on how respondents
            should answer.
            <br />
          </p>
        )}

        <label htmlFor="surveyTitle">Title (max 75 chars):</label>
        <input
          id="surveyTitle"
          type="text"
          value={title}
          onChange={(e) => {
            // Only accept up to 75 chars
            if (e.target.value.length <= 75) {
              setTitle(e.target.value);
            }
          }}
          placeholder="Enter a short title..."
          disabled={analysisInProgress}
        />
        {/* Title char count */}
        <p className="char-count">{title.length} / 75</p>

        <label htmlFor="surveyPrompt">Feedback Topic: (max 2000 chars):</label>
        <textarea
          id="surveyPrompt"
          value={prompt}
          onChange={(e) => {
            if (e.target.value.length <= 2000) {
              setPrompt(e.target.value);
            }
          }}
          rows={5}
          placeholder="Enter your feedback question or topic here..."
          disabled={analysisInProgress}
        />
        <p className="char-count">{prompt.length} / 2000</p>

        {isEditing ? (
          <button
            onClick={handleUpdateSurvey}
            className="logout-btn"
            disabled={analysisInProgress}
          >
            Save Changes
          </button>
        ) : (
          <button
            onClick={handleCreateSurvey}
            className="logout-btn"
            disabled={analysisInProgress}
          >
            Save Group
          </button>
        )}
      </div>

      {/* DISPLAY SURVEYS AS CARDS */}
      <h1>Saved Feedback Groups</h1>

      <p style={{ textAlign: "center" }}>
        <strong>This is where you can manage your feedback groups.</strong>
      </p>

      <p style={{ marginTop: "1rem" }}>
        • Edit or delete a draft.
        <br />
        • Generate a link to send to your recipients via your email.
        <br />
        • See how long it has been since you generated your link.
        <br />
        • See how many responses have been submitted to date.
        <br />
        • Close the session, and proceed to AI Analysis.
        <br />
        • View your AI Analysis.
        <br />
      </p>

      {surveys.length === 0 ? (
        <p>No feedback groups found. Create one above!</p>
      ) : (
        <div className="surveys-list">
          {surveys.map((s) => {
            const daysOpen = getDaysOpen(s);
            const isMinimized = minimizedCards.has(s.id);

            return (
              <div
                key={s.id}
                className={`survey-card ${isMinimized ? "minimized" : ""}`}
                style={{ position: "relative" }}
              >
                {/* Minimize/Expand button */}
                <button
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    background: "none",
                    border: "none",
                    color: "var(--color-text)",
                    fontSize: "1.2rem",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleMinimize(s.id)}
                  title="Minimize/Expand"
                >
                  {isMinimized ? "＋" : "－"}
                </button>

                {/* Top row: Title, Prompt, Created, Link */}
                <div className="survey-top-row">
                  <div
                    className="truncated-text"
                    onClick={() => handleShowFullText("Title", s.title)}
                    style={{ cursor: "pointer" }}
                  >
                    <strong>Title:</strong> {s.title || "(No title)"}
                  </div>

                  <div
                    className="truncated-text"
                    onClick={() => handleShowFullText("Topic", s.prompt)}
                    style={{ cursor: "pointer" }}
                  >
                    <strong>Topic:</strong> {s.prompt}
                  </div>

                  <div
                    className="truncated-text"
                    onClick={() =>
                      handleShowFullText(
                        "Created",
                        s.created_at
                          ? new Date(s.created_at).toLocaleString()
                          : "No date"
                      )
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <strong>Created:</strong>{" "}
                    {s.created_at
                      ? new Date(s.created_at).toLocaleString()
                      : "(No date)"}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      minWidth: 0,
                    }}
                  >
                    <FaRegCopy
                      style={{ cursor: "pointer" }}
                      onClick={() => copyToClipboard(s.link)}
                    />
                    <strong>Response Link:</strong>
                    {s.link ? (
                      <div style={{ minWidth: 0 }}>
                        <a
                          href={s.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className="maybe-truncate">{s.link}</span>
                        </a>
                      </div>
                    ) : (
                      <em>Not generated</em>
                    )}
                  </div>
                </div>

                {/* Collapsible area */}
                <div className="survey-card-content">
                  {/* Middle row: Max Recipients & Responses */}
                  <div className="survey-middle-row">
                    <div>
                      <strong>Response Limit:</strong> {s.max_submissions || 0}
                    </div>
                    <div>
                      <strong>Responses to Date:</strong>{" "}
                      {s.response_count || 0}
                    </div>
                  </div>

                  {/* Bottom row: Actions */}
                  <div className="survey-actions-row">
                    {s.closed ? (
                      <div>
                        <span style={{ color: "red", fontWeight: "bold" }}>
                          Survey Closed
                        </span>
                        <div style={{ marginTop: "8px" }}>
                          <button
                            className="logout-btn"
                            onClick={() => handleAnalysis(s)}
                            disabled={analysisInProgress}
                          >
                            {s.analysis_requested
                              ? "View Analysis"
                              : "AI Analysis"}
                          </button>
                        </div>
                      </div>
                    ) : s.link ? (
                      <div>
                        <p className="no-edits-allowed">
                          No edits allowed (link generated)
                        </p>
                        <div
                          style={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                          }}
                        >
                          {daysOpen !== null && (
                            <span>Days Open: {daysOpen}</span>
                          )}
                          <button
                            className="logout-btn"
                            onClick={() => handleCloseSurvey(s.id)}
                            disabled={analysisInProgress}
                          >
                            Close Session
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {s.is_free_survey ? (
                          <div className="generate-link-row">
                            <label>Responses (Free):</label>
                            <input
                              type="number"
                              value="500"
                              readOnly
                              style={{
                                width: "80px",
                                height: "25px",
                                background: "#eee",
                              }}
                            />
                            <button
                              className="logout-btn"
                              onClick={() => handleFreeGenerateLink(s.id)}
                              disabled={analysisInProgress}
                            >
                              Free Generate Link
                            </button>
                          </div>
                        ) : (
                          <div className="generate-link-row">
                            <label htmlFor={`recCount-${s.id}`}>
                              How Many Responses?
                            </label>
                            <input
                              id={`recCount-${s.id}`}
                              type="number"
                              value={rawInput}
                              placeholder="10-1000"
                              onChange={(e) => setRawInput(e.target.value)}
                              onBlur={validateRecipientInput}
                              style={{ width: "80px", height: "25px" }}
                              disabled={analysisInProgress}
                            />
                            <button
                              className="logout-btn"
                              onClick={() => handleOpenPaymentModal(s)}
                              disabled={
                                analysisInProgress ||
                                (recipientCount < 5 && recipientCount !== "")
                              }
                            >
                              Generate Link
                            </button>
                          </div>
                        )}

                        <div className="action-buttons-row">
                          <button
                            className="logout-btn"
                            onClick={() => handleEditSurvey(s)}
                            disabled={analysisInProgress}
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="logout-btn"
                            onClick={() => handleDeleteSurvey(s.id)}
                            disabled={analysisInProgress}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedSurvey && (
        <PaymentModal
          survey={selectedSurvey}
          recipientCount={recipientCount}
          onClose={(didPay) => {
            setShowPaymentModal(false);
            setSelectedSurvey(null);
            if (didPay) {
              loadSurveys();
            }
          }}
        />
      )}

      {/* Indefinite Analysis Overlay */}
      {analysisInProgress && (
        <div className="analysis-overlay">
          <div className="analysis-overlay-content">
            <h2>Please wait</h2>
            <p>
              AI is analyzing your survey. This may take a few minutes.
              Don&apos;t worry, you can walk away and come back later if you
              need to!
            </p>
            <div className="loading-bar"></div>
          </div>
        </div>
      )}

      {/* Full-Text Modal */}
      {showFullModal && (
        <div
          className="full-text-backdrop"
          onClick={() => setShowFullModal(false)}
        >
          <div
            className="full-text-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>{fullModalTitle} (Full Text)</h2>
            <p>{fullModalText || "No content"}</p>
            <button className="logout-btn" onClick={() => setShowFullModal(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Survey;
